import { useRef, useState } from "react";
import { Card, Flex, InputRef } from "antd";
import { CalendarOutlined, LockOutlined, PieChartOutlined } from "@ant-design/icons";
import MetricCard from "components/MetricCard.component";
import { ClassTypeBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { getClassAnalytics } from "hooks/queries/analytics";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { tnum } from "shared/components/TableText";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { byNumber } from "shared/reports/columnCatalogue";
import { AnalyticsRange, ClassAnalyticsRow, CustomDayRange } from "shared/types";
import { ClassCategory } from "shared/types/enum";
import PATHS from "shared/constants/paths";
import { getYtdDayRange } from "shared/utils/dayjs";
import RangeSegmented from "./RangeSegmented";
import CustomRangePicker from "./CustomRangePicker";
import ClassTypeSplitChart from "./ClassTypeSplitChart";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import { useAuth } from "context/AuthContextProvider";
import { useTableSort } from "hooks/useTableSort";
import { analyticsRangeStamp } from "shared/utils/download";
import { MAX_PDF_ROWS } from "pdf/layout";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { kelasSpec } from "pdf/reports/specs/archetype1";
import { useReportPageCount } from "./useReportPageCount";
import { textPx } from "shared/constants/zoom";

const fillRateSortValue = (row: ClassAnalyticsRow) =>
  row.type === ClassCategory.Private ? -1 : row.fill_rate;

const ClassPerformancePage = () => {
  const { state: authState } = useAuth();
  const { sortState, onSortChange } = useTableSort();
  // Defaults to YTD — customRange starts pre-populated to match so the first
  // render already queries {from,to} instead of a never-sent range=ytd token
  // (see AnalyticsRange's doc comment). isYtdActive stays derived, not
  // stored, so the pill highlights correctly whether "Tahun Ini" was clicked
  // or the admin happened to hand-pick the same Jan-through-this-month range.
  const ytdRange = getYtdDayRange();
  const [range, setRange] = useState<AnalyticsRange>("ytd");
  const [customRange, setCustomRange] = useState<CustomDayRange>(ytdRange);
  const isYtdActive = customRange?.from === ytdRange.from && customRange?.to === ytdRange.to;
  const searchInput = useRef<InputRef>(null);
  const { data, isLoading } = getClassAnalytics(range, customRange);

  // Fully uncontrolled filters on purpose: no column here may carry a
  // filteredValue, or AntD silently drops the Jenis Kelas filter.
  const columns: ReportColumn<ClassAnalyticsRow>[] = [
    reportColumn<ClassAnalyticsRow>("class", {
      dataIndex: "name",
      search: getColumnSearchProps<ClassAnalyticsRow, "name">({
        config: "name",
        searchInput,
      }),
    }),
    reportColumn<ClassAnalyticsRow>("class_type", {
      dataIndex: "type",
      render: (type: ClassCategory) => <ClassTypeBadge type={type} />,
      extra: {
        filters: [
          { value: "private", text: "Private" },
          { value: "open", text: "Open" },
        ],
        onFilter: (value, record) =>
          record.type.toString().toLowerCase().includes((value as string).toLowerCase()),
      },
    }),
    reportColumn<ClassAnalyticsRow>("sessions_completed", {
      dataIndex: "sessions_completed",
      sorter: (a, b) => a.sessions_completed - b.sessions_completed,
      // Matches the order the server already returns, so the arrow shows the
      // sort that is actually applied instead of leaving the table looking
      // unsorted.
      defaultSortOrder: "descend",
    }),
    reportColumn<ClassAnalyticsRow>("unique_clients", {
      dataIndex: "unique_clients",
      info: () => translate("analyticsPage.class.col_unique_clients_info"),
      sorter: (a, b) => a.unique_clients - b.unique_clients,
    }),
    reportColumn<ClassAnalyticsRow>("new_clients", {
      dataIndex: "new_clients",
      info: () => translate("analyticsPage.class.col_new_clients_info"),
      sorter: (a, b) => a.new_clients - b.new_clients,
    }),
    reportColumn<ClassAnalyticsRow>("fill_rate", {
      dataIndex: "fill_rate",
      info: () => translate("analyticsPage.class.col_fill_rate_info"),
      // Fill rate is an open-class metric (the KPI above averages open
      // classes only): a private session is booked as a unit, so a
      // percentage on those rows reads as a real utilization figure when
      // it isn't one. Private rows show a dash and sort below any open
      // value instead.
      render: (value: number, record) => (
        <span style={tnum}>
          {record.type === ClassCategory.Private ? "-" : `${value}%`}
        </span>
      ),
      sorter: (a, b) => fillRateSortValue(a) - fillRateSortValue(b),
    }),
    // Pembatasan Admin: the only money on an otherwise operational report.
    // The server has already nulled the figures; leaving the column out is
    // what makes the table read as if it never had one. layoutReportColumns
    // recomputes the width shares and the scroll threshold from whatever
    // column set it is given, so the name column simply gets wider.
    ...(data?.money_hidden
      ? []
      : [
          reportColumn<ClassAnalyticsRow>("booking_revenue", {
            dataIndex: "revenue",
            sorter: byNumber<ClassAnalyticsRow>("revenue"),
          }),
        ]),
  ];

  const pageCount = useReportPageCount(kelasSpec, data, sortState);

  return (
    <>
      <ReportPageHeading
        title={translate("analyticsPage.class.report_performance")}
        backTo={PATHS.ANALYTICS}
        extra={
          <ExportMenu
            reportSlug="performa-kelas"
            params={
              customRange ? { from_date: customRange.from, to_date: customRange.to } : { range }
            }
            pdf={
              data && {
                rowChoice: {
                  pagesFor: pageCount.pagesFor,
                  prepare: pageCount.prepare,
                  available: Math.min(data.table.length, MAX_PDF_ROWS),
                  total: data.table.length,
                },
                fileName: `spott-performa-kelas-${analyticsRangeStamp(range, customRange)}.pdf`,
                render: async (maxRows) => {
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: "performa-kelas",
                    studio: authState.business?.name ?? "",
                    data,
                    sortState,
                    maxRows,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <span style={{ fontSize: textPx(13), color: colors.ink2 }}>
            {data?.range_label}
          </span>
          <Flex align="center" gap={10} wrap="wrap">
            <CustomRangePicker value={isYtdActive ? null : customRange} onChange={setCustomRange} />
            <RangeSegmented
              value={isYtdActive ? "ytd" : range}
              active={!customRange || isYtdActive}
              onChange={(value) => {
                setCustomRange(value === "ytd" ? ytdRange : null);
                setRange(value);
              }}
            />
          </Flex>
        </Flex>
        <Flex gap={16} wrap="wrap">
          <MetricCard
            icon={<CalendarOutlined />}
            title={translate("analyticsPage.class.kpi_open_sessions")}
            value={data?.open_sessions.value ?? 0}
          />
          <MetricCard
            icon={<LockOutlined />}
            title={translate("analyticsPage.class.kpi_private_sessions")}
            value={data?.private_sessions.value ?? 0}
          />
          <MetricCard
            icon={<PieChartOutlined />}
            title={translate("analyticsPage.class.kpi_fill_rate")}
            value={`${data?.avg_fill_rate.value ?? 0}%`}
          />
        </Flex>
        {/* Sessions only. A revenue split used to sit beside this one and was
            removed: earned revenue comes from credits consumed, so a booking
            on an unlimited membership contributes nothing to it. The share
            between open and private therefore understates whichever side is
            more membership-driven, and a proportion bar states that far too
            confidently. The per-class figure in the table is an honest row
            fact; the side-by-side proportion was not. */}
        <Card styles={{ body: { padding: 22 } }} loading={isLoading}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: textPx(16), color: colors.ink }}>
            {translate("analyticsPage.class.split_chart_heading")}
          </div>
          <div style={{ fontSize: textPx(12), color: colors.ink3, marginTop: 4, marginBottom: 18 }}>
            {translate("analyticsPage.class.split_chart_desc")}
          </div>
          <ClassTypeSplitChart
            openValue={data?.open_sessions.value ?? 0}
            privateValue={data?.private_sessions.value ?? 0}
            totalLabel={translate("analyticsPage.class.total_label")}
            totalSuffix={translate("analyticsPage.class.total_suffix_sessions")}
          />
        </Card>
        <Card styles={{ body: { padding: 18 } }}>
          <ReportTable<ClassAnalyticsRow>
            rowKey="class_type_id"
            loading={isLoading}
            columns={columns}
            dataSource={data?.table ?? []}
            pageMemory="analyticsClassPerformance"
            onSortChange={onSortChange}
          />
        </Card>
      </Flex>
    </>
  );
};

export default ClassPerformancePage;
